// 표 위젯(`InputWidget.widget==='table'`) 전용 속성 축의 **순수 층** — 키 목록·노출 순서·노출 게이트.
//
// 화면(컨트롤 JSX)은 EditorPage의 디스크립터가 그리고, 이 파일은 "어떤 축이 언제 보이는가"만
// 소유한다. 여기로 뺀 이유는 두 가지다:
//  1) 노출 순서를 **단일 패널과 양식 표 셀 패널이 한 배열로** 공유해야 한다 — 패널마다 손으로
//     나열하면 한쪽에만 붙는 비대칭이 생긴다(표 축이 셀 패널에서 통째로 빠져 있던 병 자체다,
//     2026-08-07 저작 UI 공백 감사 ③).
//  2) 게이트(`applicable`)는 임의로 넓히면 **안 먹는 자리에 컨트롤이 뜨고** 좁히면 저장값이 있는데
//     편집 진입점이 없는 유령 상태가 된다. 순수 술어로 떼어 놓아야 spec으로 못 박을 수 있다.

import type { ComposerWidget, DataDef, InputWidget } from '@safetyhub/shared';
import { resolveTableFields } from '@safetyhub/report-composer';

/**
 * 표 위젯 전용 속성 키 — EditorPage의 `WidgetPropKey`가 이 union을 흡수한다.
 * 새 표 축은 여기에 추가하면 아래 Record 3개가 **tsc로** 누락을 강제한다.
 */
export type TableWidgetPropKey =
  | 'tableHideHeader'
  | 'tableBorder'
  | 'tableDisplayColumns'
  | 'tableFillDirection'
  | 'tableGroupByField'
  | 'tableRowClickToggleField'
  | 'tableColumns';

/** 표 축의 공통 게이트 — 배치 위치(본문/셀/반복)와 무관하게 `widget==='table'`이면 노출 대상. */
export function isTableWidget(w: ComposerWidget): w is InputWidget {
  return w.kind === 'input' && w.widget === 'table';
}

/** 노출 순번(작을수록 위) — 종전 단일 패널의 표시 순서 그대로. */
const TABLE_WIDGET_PROP_RANK: Record<TableWidgetPropKey, number> = {
  tableHideHeader: 0,
  tableBorder: 1,
  tableDisplayColumns: 2,
  tableFillDirection: 3,
  tableGroupByField: 4,
  tableRowClickToggleField: 5,
  tableColumns: 6,
};

/** 표 축 노출 순서 — **단일 속성 패널과 표 셀 패널이 이 배열 하나를** 순회한다. */
export const TABLE_WIDGET_PROP_ORDER: TableWidgetPropKey[] = (
  Object.keys(TABLE_WIDGET_PROP_RANK) as TableWidgetPropKey[]
).sort((a, b) => TABLE_WIDGET_PROP_RANK[a] - TABLE_WIDGET_PROP_RANK[b]);

/**
 * 복수 선택 일괄 편집(bulk) 대상 여부.
 *  - **표시 축**(헤더 숨김·테두리·표시 단수·채움 방향)은 여러 표에 같은 값을 주는 게 자연스럽다.
 *  - 나머지 셋은 선택지·편집 대상이 **그 위젯 자신의 `fields[]`**에서 나온다 — 복수 선택에 일괄
 *    적용하면 남의 칼럼 key를 박거나 남의 순서·너비를 덮어쓴다.
 */
export const TABLE_WIDGET_PROP_BULK: Record<TableWidgetPropKey, boolean> = {
  tableHideHeader: true,
  tableBorder: true,
  tableDisplayColumns: true,
  tableFillDirection: true,
  tableGroupByField: false,
  tableRowClickToggleField: false,
  tableColumns: false,
};

/** bulk 대상만 노출 순서대로 — EditorPage의 `WIDGET_PROP_BULK_ORDER` 끝에 이어 붙는다. */
export const TABLE_WIDGET_BULK_PROP_ORDER: TableWidgetPropKey[] = TABLE_WIDGET_PROP_ORDER.filter(
  (k) => TABLE_WIDGET_PROP_BULK[k],
);

/**
 * 축별 노출 게이트 — 디스크립터 `applicable`의 정본.
 * 전부 `isTableWidget` AND로 시작한다(표가 아닌 위젯에는 어느 축도 뜨지 않는다).
 *
 * 칼럼 의존 게이트는 `w.fields` 직접 읽기가 아니라 **resolveTableFields**(사본 우선 + 정본 폴백)로
 * 판정한다 — P3 로드 정규화가 사본을 지운 참조 모드 위젯(fields 부재, 정본 live 참조)에서도
 * 칼럼·그룹·행 클릭 축이 계속 떠야 한다(fields 부재를 "칼럼 없음"으로 읽으면 정규화 직후
 * 편집 진입점이 통째로 사라지는 유령 상태). dataDefs 미제공 호출부는 종전과 동일하게 사본만 본다.
 */
export const TABLE_WIDGET_PROP_APPLICABLE: Record<
  TableWidgetPropKey,
  (w: ComposerWidget, dataDefs?: DataDef[]) => boolean
> = {
  tableHideHeader: isTableWidget,
  tableBorder: isTableWidget,
  tableDisplayColumns: isTableWidget,
  // 다단일 때만 의미가 있는 축 — 1단이면 rc가 읽지 않는다(안 먹는 자리에 컨트롤을 띄우지 않는다).
  tableFillDirection: (w) => isTableWidget(w) && (w.displayColumns ?? 1) >= 2,
  // 칼럼 선언이 없으면 그룹으로 고를 것이 없다.
  tableGroupByField: (w, dataDefs) =>
    isTableWidget(w) && resolveTableFields(w, dataDefs).length > 0,
  // 토글할 checkbox 칼럼이 있어야 성립.
  tableRowClickToggleField: (w, dataDefs) =>
    isTableWidget(w) && resolveTableFields(w, dataDefs).some((f) => f.widget === 'checkbox'),
  // 게이트가 `> 1`이면 **칼럼 하나짜리 표**(관련법규 조문 등)에서 아예 저작할 수 없다 — 저장값은
  // 있는데 편집 진입점이 없는 유령 상태가 된다(2026-08-06 리뷰). 순서(↑↓)만 칼럼이 2개 이상일 때
  // 의미가 있어 편집기 안에서 가린다.
  tableColumns: (w, dataDefs) => isTableWidget(w) && resolveTableFields(w, dataDefs).length >= 1,
};
